<script setup>
import { computed, ref } from "vue";
import { ArchiveBoxIcon } from "@heroicons/vue/24/outline";

const props = defineProps({ disabled: Boolean, busy: Boolean });
const emit = defineEmits(["open", "close"]);
const offset = ref(0);
const dragging = ref(false);
const ready = computed(() => Math.abs(offset.value) >= 88);
let gesture = null;
let suppressClickUntil = 0;

function start(event) {
  if (
    props.disabled ||
    props.busy ||
    event.isPrimary === false ||
    event.button > 0
  )
    return;
  gesture = {
    id: event.pointerId,
    x: event.clientX,
    y: event.clientY,
    axis: null,
  };
  // Capture on the stationary row before its content starts moving. Touch
  // pointers initially capture the nested element under the finger.
  event.currentTarget.setPointerCapture?.(event.pointerId);
}
function move(event) {
  if (!gesture || gesture.id !== event.pointerId) return;
  const dx = event.clientX - gesture.x;
  const dy = event.clientY - gesture.y;
  if (!gesture.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 10) {
    if (Math.abs(dx) > Math.abs(dy) * 1.3) gesture.axis = "x";
    else if (Math.abs(dy) > Math.abs(dx) * 1.3) gesture.axis = "y";
  }
  if (gesture.axis !== "x") return;
  dragging.value = true;
  suppressClickUntil = Date.now() + 500;
  offset.value = Math.sign(dx) * Math.min(Math.abs(dx), 160);
}
function finish(event, cancelled = false) {
  if (!gesture || gesture.id !== event.pointerId) return;
  const shouldClose = !cancelled && gesture.axis === "x" && ready.value;
  if (gesture.axis === "x") suppressClickUntil = Date.now() + 500;
  gesture = null;
  dragging.value = false;
  offset.value = 0;
  if (shouldClose) emit("close");
}
function lostCapture(event) {
  // lostpointercapture bubbles when capture transfers from a child to the
  // row. Only losing the row's own capture cancels this gesture.
  if (event.target === event.currentTarget) finish(event, true);
}
function open(event) {
  if (props.busy || Date.now() < suppressClickUntil) return;
  emit("open", event);
}
</script>

<template>
  <div
    class="swipe-row"
    :class="{ ready }"
    @pointerdown="start"
    @pointermove="move"
    @pointerup="finish($event)"
    @pointercancel="finish($event, true)"
    @lostpointercapture="lostCapture"
  >
    <div
      class="swipe-action"
      aria-hidden="true"
      :style="{ justifyContent: offset < 0 ? 'flex-end' : 'flex-start' }"
    >
      <ArchiveBoxIcon class="h-6 w-6" />
      <span>{{ ready ? "Release to close" : "Close ticket" }}</span>
    </div>
    <button
      type="button"
      class="swipe-content bg-white dark:bg-slate-800"
      :class="{ dragging }"
      :style="{ transform: `translateX(${offset}px)` }"
      :disabled="busy"
      :aria-busy="busy"
      @dragstart.prevent
      @click="open"
    >
      <slot />
    </button>
  </div>
</template>

<style scoped>
.swipe-row {
  position: relative;
  overflow: hidden;
  background: #15803d;
  touch-action: pan-y pinch-zoom;
}
.swipe-row.ready {
  background: #166534;
}
.swipe-action {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 20px;
  color: white;
  font-size: 13px;
}
.swipe-content {
  position: relative;
  display: block;
  width: 100%;
  text-align: left;
  padding: 16px 12px;
  user-select: none;
  -webkit-user-select: none;
  transition: transform 180ms ease;
}
.swipe-content.dragging {
  transition: none;
  user-select: none;
}
.swipe-content:disabled {
  opacity: 0.7;
}
@media (prefers-reduced-motion: reduce) {
  .swipe-content {
    transition: none;
  }
}
</style>
